import { Box, CircularProgress, Container } from "@mui/material";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import ChatActiveTurn from "./ChatActiveTurn";
import ChatHistoryList from "./ChatHistoryList";
import { ChatTurn } from "./chatBotTypes";

type Props = {
  visible: boolean;
  isDrawerOpen: boolean;
  isLoadingHistory: boolean;
  isLoadingMoreHistory: boolean;
  hasMoreHistory: boolean;
  onLoadMoreHistory: () => void;
  chatHistory: ChatTurn[];
  isWaitingForResponse: boolean;
  currentChatTranscript: string;
  currentUserTranscript: string;
  currentThoughtTitle: string;
  chatTypewriterSpeed: number;
  currentTurnScrollTrigger: number;
  authId: string;
  platformId?: string;
  leagueId?: string;
  teamName?: string;
  onShareMessage: (messageId: string) => void;
  isCheckmark?: boolean;
};

const SCROLL_LOAD_THRESHOLD_PX = 300;

const areViewportPropsEqual = (prev: Props, next: Props) =>
  prev.visible === next.visible &&
  prev.isDrawerOpen === next.isDrawerOpen &&
  prev.isLoadingHistory === next.isLoadingHistory &&
  prev.isLoadingMoreHistory === next.isLoadingMoreHistory &&
  prev.hasMoreHistory === next.hasMoreHistory &&
  prev.onLoadMoreHistory === next.onLoadMoreHistory &&
  prev.chatHistory === next.chatHistory &&
  prev.isWaitingForResponse === next.isWaitingForResponse &&
  prev.currentChatTranscript === next.currentChatTranscript &&
  prev.currentUserTranscript === next.currentUserTranscript &&
  prev.currentThoughtTitle === next.currentThoughtTitle &&
  prev.chatTypewriterSpeed === next.chatTypewriterSpeed &&
  prev.currentTurnScrollTrigger === next.currentTurnScrollTrigger &&
  prev.authId === next.authId &&
  prev.platformId === next.platformId &&
  prev.leagueId === next.leagueId &&
  prev.teamName === next.teamName &&
  prev.onShareMessage === next.onShareMessage &&
  prev.isCheckmark === next.isCheckmark;

const ChatMessagesViewport = ({
  visible,
  isDrawerOpen,
  isLoadingHistory,
  isLoadingMoreHistory,
  hasMoreHistory,
  onLoadMoreHistory,
  chatHistory,
  isWaitingForResponse,
  currentChatTranscript,
  currentUserTranscript,
  currentThoughtTitle,
  chatTypewriterSpeed,
  currentTurnScrollTrigger,
  authId,
  platformId,
  leagueId,
  teamName,
  onShareMessage,
  isCheckmark,
}: Props) => {
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const currentTurnAnchorRef = useRef<HTMLDivElement | null>(null);
  const latestTurnRef = useRef<HTMLDivElement | null>(null);
  const bottomSpacerRef = useRef<HTMLDivElement | null>(null);
  const hasActiveTurnRef = useRef(false);
  const needsHistoryPinRef = useRef(false);
  // false until first pin after history load — avoids flash of wrong scroll position
  const [isHistoryScrollReady, setIsHistoryScrollReady] = useState(true);
  const pendingScrollRestoreHeightRef = useRef<number | null>(null);

  // While a new turn is in progress, no history turn is "latest" for thumbs (hover applies)
  const hasActiveTurn =
    isWaitingForResponse ||
    currentChatTranscript.length > 0 ||
    currentUserTranscript.length > 0;
  hasActiveTurnRef.current = hasActiveTurn;

  // Dynamic spacer = viewport − latest turn height (not a fixed 65vh).
  // Lets the latest question sit at the top; remaining space is natural black.
  const syncBottomSpacer = useCallback(() => {
    const container = chatContainerRef.current;
    const spacer = bottomSpacerRef.current;
    if (!container || !spacer) return;

    const latestEl = hasActiveTurnRef.current
      ? currentTurnAnchorRef.current
      : latestTurnRef.current;
    const prevTop = latestEl?.getBoundingClientRect().top;
    const latestH = latestEl?.offsetHeight ?? 0;
    const spacerH = Math.max(0, container.clientHeight - latestH);
    spacer.style.minHeight = `${spacerH}px`;

    if (!latestEl) return;

    const containerRect = container.getBoundingClientRect();
    const paddingTop = parseFloat(getComputedStyle(container).paddingTop) || 0;
    const idealTop = containerRect.top + paddingTop;

    if (hasActiveTurnRef.current) {
      // While streaming/thinking: lock the active turn to the top so spacer shrink
      // (and waiting→streaming content growth) cannot clamp scroll downward.
      const nextTop = latestEl.getBoundingClientRect().top;
      const drift = nextTop - idealTop;
      if (Math.abs(drift) > 0.5) {
        container.scrollTop += drift;
      }
      return;
    }

    // History only: preserve visual position when spacer / turn height changes
    if (prevTop != null) {
      const nextTop = latestEl.getBoundingClientRect().top;
      const delta = nextTop - prevTop;
      if (delta !== 0) {
        container.scrollTop += delta;
      }
    }
  }, []);

  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container || isLoadingHistory || isLoadingMoreHistory || !hasMoreHistory) {
      return;
    }

    if (container.scrollTop <= SCROLL_LOAD_THRESHOLD_PX) {
      pendingScrollRestoreHeightRef.current = container.scrollHeight;
      onLoadMoreHistory();
    }
  }, [hasMoreHistory, isLoadingHistory, isLoadingMoreHistory, onLoadMoreHistory]);

  const pinLatestToTop = useCallback(() => {
    syncBottomSpacer();
    const container = chatContainerRef.current;
    const target = hasActiveTurnRef.current
      ? currentTurnAnchorRef.current
      : latestTurnRef.current;
    if (!container || !target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const paddingTop = parseFloat(getComputedStyle(container).paddingTop) || 0;
    // +1px clears subpixel bleed of the previous turn's thumbs at the top edge
    container.scrollTop += targetRect.top - containerRect.top - paddingTop + 1;
  }, [syncBottomSpacer]);

  // When history fetch starts, hide messages until we can pin the latest turn (no flash).
  useEffect(() => {
    if (!isLoadingHistory) return;
    needsHistoryPinRef.current = true;
    setIsHistoryScrollReady(false);
  }, [isLoadingHistory]);

  // Pin before paint once history is in the DOM — replaces the old 100ms timeout flash.
  useLayoutEffect(() => {
    if (!isDrawerOpen || isLoadingHistory) return;
    if (!needsHistoryPinRef.current) return;

    if (chatHistory.length === 0) {
      needsHistoryPinRef.current = false;
      setIsHistoryScrollReady(true);
      return;
    }

    pinLatestToTop();
    needsHistoryPinRef.current = false;
    setIsHistoryScrollReady(true);
  }, [isDrawerOpen, isLoadingHistory, chatHistory, pinLatestToTop]);

  // New user question: pin the active turn to the top.
  // IMPORTANT: only depend on currentTurnScrollTrigger — do NOT re-run when
  // hasActiveTurn flips on complete (that caused the thumbs mount jump).
  useEffect(() => {
    if (isLoadingMoreHistory || pendingScrollRestoreHeightRef.current === null) {
      return;
    }

    const container = chatContainerRef.current;
    if (!container) return;

    const previousScrollHeight = pendingScrollRestoreHeightRef.current;
    pendingScrollRestoreHeightRef.current = null;

    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight - previousScrollHeight;
    });
  }, [isLoadingMoreHistory, chatHistory.length]);

  useEffect(() => {
    if (currentTurnScrollTrigger === 0) return;
    const timeoutId = setTimeout(() => {
      pinLatestToTop();
    }, 120);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentTurnScrollTrigger, pinLatestToTop]);

  // First streamed token: re-lock pin before paint (thinking → response content swap).
  const hadTranscriptRef = useRef(false);
  useLayoutEffect(() => {
    const hasTranscript = currentChatTranscript.length > 0;
    const justStartedStreaming = hasTranscript && !hadTranscriptRef.current;
    hadTranscriptRef.current = hasTranscript;
    if (!justStartedStreaming) return;
    pinLatestToTop();
  }, [currentChatTranscript, pinLatestToTop]);

  // After active → history swap: only sync spacer (delta compensation). Do NOT
  // pinLatestToTop here — that was the visible "scroll back up" after the stream jump.
  const wasActiveTurnRef = useRef(false);
  useLayoutEffect(() => {
    const wasActive = wasActiveTurnRef.current;
    wasActiveTurnRef.current = hasActiveTurn;
    if (!wasActive || hasActiveTurn) return;
    syncBottomSpacer();
  }, [hasActiveTurn, chatHistory, syncBottomSpacer]);

  // Keep spacer in sync as the latest turn grows/shrinks (stream, complete, resize).
  useEffect(() => {
    if (!visible) return;

    const frameId = requestAnimationFrame(() => syncBottomSpacer());

    const container = chatContainerRef.current;
    if (!container) {
      return () => cancelAnimationFrame(frameId);
    }

    const resizeObserver = new ResizeObserver(() => syncBottomSpacer());
    resizeObserver.observe(container);

    const latestEl = hasActiveTurn ? currentTurnAnchorRef.current : latestTurnRef.current;
    if (latestEl) resizeObserver.observe(latestEl);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [
    visible,
    hasActiveTurn,
    chatHistory,
    currentChatTranscript,
    isWaitingForResponse,
    syncBottomSpacer,
  ]);

  if (!visible) return null;

  return (
    <Container
      ref={chatContainerRef}
      onScroll={handleScroll}
      maxWidth={isMobileWebBrowser() || mobile ? "sm" : "md"}
      sx={{
        p: 1.5,
        pr: 2,
        pl: 2,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowAnchor: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {isLoadingMoreHistory && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={20} sx={{ color: "#FF672D" }} />
        </Box>
      )}

      {isLoadingHistory && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} sx={{ color: "#FF672D" }} />
        </Box>
      )}

      {/* Opacity 0 until pinned so a wrong scroll position never paints (fixes open flash). */}
      <Box sx={{ opacity: isHistoryScrollReady ? 1 : 0 }}>
        <ChatHistoryList
          turns={chatHistory}
          authId={authId}
          platformId={platformId}
          leagueId={leagueId}
          teamName={teamName}
          onShareMessage={onShareMessage}
          isCheckmark={isCheckmark}
          hasActiveTurn={hasActiveTurn}
          latestTurnRef={latestTurnRef}
        />
      </Box>

      <ChatActiveTurn
        isWaitingForResponse={isWaitingForResponse}
        currentChatTranscript={currentChatTranscript}
        currentUserTranscript={currentUserTranscript}
        currentThoughtTitle={currentThoughtTitle}
        chatTypewriterSpeed={chatTypewriterSpeed}
        anchorRef={currentTurnAnchorRef}
      />

      {/* Dynamic bottom spacer: fills leftover viewport under the latest turn (Gemini-style). */}
      <Box ref={bottomSpacerRef} sx={{ flexShrink: 0 }} aria-hidden />
    </Container>
  );
};

export default React.memo(ChatMessagesViewport, areViewportPropsEqual);
